import { CenteredLayout, ContentHeader, Css, useComputed, useModal } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import {
  AiProposalsFormValue,
  aiProposalsConfig,
  entityIsRemoved,
  flattenEntityProposals,
  hasPendingProposals,
  indexEntityProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { parseAiNumber } from "src/components/formFields/ai/utils/bindAiProposal";
import { resolvePendingAiProposals } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  AiFieldProposalStatus,
  FeatureFlagType,
  IncrementalCollectionOp,
  SaveReadyPlanOptionInput,
  SaveReadyPlanProgramDataInput,
} from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { ElevationCard } from "src/routes/libraries/plan-package-v2/components/elevations/ElevationCard";
import {
  PlanPackageElevationsV2Query,
  usePlanPackageElevationsV2Query,
  useSavePlanPackageElevationMutation,
} from "src/routes/libraries/plan-package-v2/components/ElevationsTab.generated";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import { queryResult } from "src/utils";
import { AddEditElevationModal } from "./elevations/AddEditElevationModal";

/** The square footage the import proposes per elevation; room counts are PLAN-1816. */
const elevationSqftFields = [
  "sellableAboveGroundSqft",
  "sellableBelowGroundSqft",
  "netBuildableSqft",
  "grossBuildableSqft",
  "permittableSqft",
  "imperviousSqft",
] as const;

type ElevationsTabProps = { planPackageId: string; versionId: string };

export function ElevationsTab({ planPackageId, versionId }: ElevationsTabProps) {
  const query = usePlanPackageElevationsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage }) => <DataView planPackage={planPackage} />);
}

type DataViewProps = { planPackage: PlanPackageElevationsV2Query["planPackage"] };

function DataView({ planPackage }: DataViewProps) {
  const { openModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageElevationMutation();
  const aiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const elevations = useMemo(() => toElevations(planPackage), [planPackage]);

  const proposals = useMemo(
    // `entityType: [ELEVATION]` filters the query; the entity itself is still a ReadyPlanOption
    () => indexEntityProposals(aiEnabled ? planPackage.aiProposal?.entityProposals : undefined, "ReadyPlanOption"),
    [planPackage, aiEnabled],
  );

  const formState = useFormState({
    config: aiProposalsConfig,
    init: { input: planPackage, map: (pp) => mapToForm(pp, aiEnabled) },
  });

  const hasPending = useComputed(() => hasPendingProposals(formState.fieldProposals.rows), [formState]);

  const afpStateByElevation = useComputed(() => {
    const byProposalId = new Map(formState.fieldProposals.rows.map((row) => [row.id.value, row] as const));
    return new Map(
      elevations.map((e) => [
        e.id,
        (proposals.get(e.id)?.fieldProposals ?? []).map((fp) => byProposalId.get(fp.id)).compact(),
      ]),
    );
  }, [formState, elevations, proposals]);

  async function saveProposals() {
    const resolutions = toResolveInputs(formState.fieldProposals.changedValue);
    if (resolutions.isEmpty) return;

    // Ignoring the import's proposed elevation drops the row it created for review.
    const options = elevations.flatMap((e): SaveReadyPlanOptionInput[] => {
      const proposal = proposals.get(e.id);
      const afpsByElevation = afpStateByElevation.get(e.id) ?? [];
      if (!proposal || !entityIsRemoved(proposal.type.code, afpsByElevation)) return [];
      return [{ id: e.id, active: false }];
    });

    const programDatas = elevations.flatMap((e): SaveReadyPlanProgramDataInput[] => {
      const afpsByElevation = afpStateByElevation.get(e.id) ?? [];
      const programData = Object.fromEntries(
        elevationSqftFields.flatMap((fieldName) => {
          const proposal = afpsByElevation.find((afp) => afp.fieldName.value === fieldName);
          if (proposal?.status.value !== AiFieldProposalStatus.Accepted) return [];
          return [[fieldName, parseAiNumber(proposal.fieldValue.value) ?? null]];
        }),
      );
      if (Object.keys(programData).isEmpty) return [];
      const own = e.programDatas.find((pd) => pd.readyPlanOptions.length === 1);
      return [
        own
          ? { id: own.id, op: IncrementalCollectionOp.Include, programData }
          : { op: IncrementalCollectionOp.Include, readyPlanOptionIds: [e.id], programData },
      ];
    });

    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          ...(options.nonEmpty ? { options } : {}),
          ...(programDatas.nonEmpty ? { programDatas } : {}),
          proposals: resolutions,
        },
      },
    });
    const saved = data?.savePlanPackage.planPackage;
    if (saved && saved.version.id !== planPackage.version.id) {
      formState.update(saved);
      navigateToRpav(saved.version.id);
    }
  }

  async function reconcileAll(decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) {
    resolvePendingAiProposals(formState.fieldProposals.rows, decision);
    await saveProposals();
  }

  return (
    <CenteredLayout size="lg">
      {aiEnabled && hasPending && (
        <div css={Css.px3.mb3.$}>
          <PlanPackageAiReviewBanner hasPending={hasPending} warnings={0} onReconcileAll={reconcileAll} />
        </div>
      )}
      <ContentHeader
        title="Elevations"
        description=" Elevations are sets of exterior options that represent a particular architectural style. Exterior Schemes,
              which are styling choices within Elevations, are configured in Product Offerings."
        actions={[
          {
            label: "Add Elevation",
            icon: "plus",
            variant: "secondary",
            disabled,
            onClick: () => openModal({ size: "lg", content: <AddEditElevationModal planPackage={planPackage} /> }),
          },
        ]}
        xss={Css.px3.$}
      />
      <div css={Css.p3.df.fdc.gap3.$}>
        {elevations.isEmpty ? (
          <div css={Css.py3.tac.$}>No elevations added.</div>
        ) : (
          <div css={Css.dg.gtc("repeat(auto-fill, minmax(330px, 1fr))").gap3.$}>
            {elevations.map((e) => (
              <ElevationCard
                key={e.id}
                elevation={e}
                planCode={planPackage.displayCode}
                proposalType={proposals.get(e.id)?.type.code}
                fieldProposals={afpStateByElevation.get(e.id) ?? []}
                onClick={
                  disabled
                    ? undefined
                    : () =>
                        openModal({
                          size: "lg",
                          content: (
                            <AddEditElevationModal
                              planPackage={planPackage}
                              elevation={e}
                              proposal={proposals.get(e.id)}
                            />
                          ),
                        })
                }
              />
            ))}
          </div>
        )}
      </div>
    </CenteredLayout>
  );
}

function toElevations(planPackage: PlanPackageElevationsV2Query["planPackage"]) {
  return planPackage.options.filter((o) => o.active && o.type.isElevation).sortBy((o) => o.order);
}

function mapToForm(planPackage: PlanPackageElevationsV2Query["planPackage"], aiEnabled: boolean): AiProposalsFormValue {
  const proposals = indexEntityProposals(
    aiEnabled ? planPackage.aiProposal?.entityProposals : undefined,
    "ReadyPlanOption",
  );
  return {
    fieldProposals: flattenEntityProposals(
      toElevations(planPackage)
        .map((e) => proposals.get(e.id))
        .compact(),
    ),
  };
}
